// src/pages/Profile.jsx
//
// KEY FIX: Added isInitialized check from AuthContext.
// Profile now waits until auth is confirmed before deciding to redirect.
// This prevents the race condition on production where user briefly appears
// null before localStorage has been read.

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { fetchUserTestDrives, rescheduleTestDrive, cancelTestDrive } from '../services/testDrive';
import { shortlistService } from '../services/shortlist';
import { useShortlist } from '../context/ShortlistContext';
import { bookingService } from '../services/bookingService';
import { orderService } from '../services/orderService';
import RescheduleModal from '../components/RescheduleModal';
import '../styles/Profile.css';
import MySells from '../pages/MySells';
import { usersAPI } from '../services/users';
import { useSellRequest } from '../context/SellRequestContext';
import MyOrdersTab from '../components/orders/MyOrdersTab';
import PriceAcceptanceTab from '../components/PriceAcceptanceTab.jsx';
import PaymentHistoryTab from '../components/payments/PaymentHistoryTab';
import ReferAndEarn from '../components/referral/ReferAndEarn';
import { getImageUrl } from '../services/api';

const BLOCK   = { display: 'block', width: '100%', boxSizing: 'border-box' };
const COLLIST = { display: 'flex', flexDirection: 'column', gap: 20, width: '100%' };

const Profile = () => {
  const { user, logout, updateUser, isInitialized } = useAuth();
  const navigate = useNavigate();
  const { refreshShortlist } = useShortlist();
  const [activeTab, setActiveTab] = useState('overview');
  const { sellRequests } = useSellRequest();
  const [testDrivesCount,       setTestDrivesCount]       = useState(0);
  const [localShortlistedCount, setLocalShortlistedCount] = useState(0);
  const [bookingsCount,         setBookingsCount]         = useState(0);
  const [ordersCount,           setOrdersCount]           = useState(0);
  const [paymentsCount,         setPaymentsCount]         = useState(0);
  const fileInputRef = React.useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // FIXED: only redirect after auth is initialized AND user is null
  // isInitialized is always true (sync init), but keeps the logic explicit
  // and safe against any future async change.
  useEffect(() => {
    if (isInitialized && user === null) {
      navigate('/login', { replace: true });
    }
  }, [isInitialized, user, navigate]);

  useEffect(() => {
    if (!user) return;

    const fetchCounts = async () => {
      try {
        const testDrives = await fetchUserTestDrives();
        setTestDrivesCount(testDrives.length);
      } catch { /* non-critical */ }

      try {
        const shortlistResponse = await shortlistService.getShortlistedCars(1);
        if (shortlistResponse.success)
          setLocalShortlistedCount(shortlistResponse.pagination?.total_count || 0);
      } catch { /* non-critical */ }

      try {
        const bookingsData = await bookingService.getAllBookings();
        const bookings = bookingsData.bookings || bookingsData || [];
        setBookingsCount(Array.isArray(bookings) ? bookings.length : 0);
      } catch { /* non-critical */ }

      try {
        const ordersData = await orderService.getAllOrders();
        setOrdersCount(Array.isArray(ordersData) ? ordersData.length : 0);
      } catch { /* non-critical */ }

      try {
        const { paymentService } = await import('../services/paymentService');
        const res = await paymentService.getPaymentHistory();
        setPaymentsCount((res.data || []).length);
      } catch { /* non-critical */ }
    };

    fetchCounts();
  }, [user]);

  const handleAvatarClick = () => fileInputRef.current?.click();

  const handleProfileImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setUploadingImage(true);
      const response = await usersAPI.uploadProfileImage(file);
      updateUser({ ...user, profile_image_url: response.data.profile_image_url });
    } catch { alert('Failed to upload profile image'); }
    finally { setUploadingImage(false); }
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  const handleRemoveProfileImage = async () => {
    try { await usersAPI.removeProfileImage(); updateUser({ ...user, profile_image_url: null }); }
    catch (error) { console.error('Failed to remove profile image', error); }
  };

  const handleShortlistUpdate = async () => {
    await refreshShortlist();
    try {
      const shortlistResponse = await shortlistService.getShortlistedCars(1);
      if (shortlistResponse.success)
        setLocalShortlistedCount(shortlistResponse.pagination?.total_count || 0);
    } catch { /* non-critical */ }
  };

  const getUserDisplayName = () => {
    if (user?.name?.trim()) return user.name;
    if (user?.phone) return user.phone;
    if (user?.email && !user.email.includes('temp.aucauto.com')) return user.email.split('@')[0];
    return 'User';
  };

  const getAvatarInitial = () => {
    if (user?.name?.trim()) return user.name.charAt(0).toUpperCase();
    if (user?.phone) return user.phone.charAt(0);
    if (user?.email && !user.email.includes('temp.aucauto.com')) return user.email.charAt(0).toUpperCase();
    return 'U';
  };

  const getUserIdentifier = () => {
    if (user?.phone) return `+91 ${user.phone}`;
    if (user?.email && !user.email.includes('temp.aucauto.com')) return user.email;
    return 'No contact info';
  };

  // Show nothing while auth initializes (prevents flicker)
  if (!isInitialized) return null;
  if (!user) return null;

  const sellRequestsCount = Array.isArray(sellRequests) ? sellRequests.length : 0;

  const navItems = [
    { id: 'overview',         icon: '📊', label: 'Overview'                                     },
    { id: 'saved-cars',       icon: '❤️', label: 'Saved Cars',      badge: localShortlistedCount },
    { id: 'test-drives',      icon: '🚗', label: 'Test Drives',     badge: testDrivesCount        },
    { id: 'bookings',         icon: '📅', label: 'My Bookings',     badge: bookingsCount          },
    { id: 'my-orders',        icon: '🛒', label: 'My Orders',       badge: ordersCount            },
    { id: 'payment-history',  icon: '💳', label: 'Payment History', badge: paymentsCount          },
    { id: 'refer-earn',       icon: '🎁', label: 'Refer & Earn'                                  },
    { id: 'car-inspection',   icon: '🔍', label: 'Car Inspection'                                },
    { id: 'price-acceptance', icon: '💰', label: 'Price Approval'                                },
    { id: 'settings',         icon: '⚙️', label: 'Settings'                                     },
  ];

  return (
    <div className="profile-container">
      <div className="profile-header">
        <div className="profile-header-bg"></div>
        <div className="profile-header-content">
          <div className="profile-avatar-wrap">
            <div className="avatar-circle" onClick={handleAvatarClick} style={{ cursor: 'pointer', position: 'relative' }}>
              {user.profile_image_url
                ? <img src={getImageUrl(user.profile_image_url)} alt="Profile" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                : <span className="avatar-initial">{getAvatarInitial()}</span>
              }
              {uploadingImage && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12 }}>
                  ...
                </div>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleProfileImageChange} />
          </div>
          <div className="profile-info">
            <h1 className="profile-name">{getUserDisplayName()}</h1>
            <p className="profile-email">{getUserIdentifier()}</p>
            <div className="profile-badges">
              <span className="badge verified">✓ Verified</span>
              <span className="badge member-since">Member since 2025</span>
            </div>
          </div>
        </div>
      </div>

      <div className="profile-body">
        <aside className="profile-sidebar">
          <nav className="profile-nav">
            {navItems.map(item => (
              <button
                key={item.id}
                className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
                onClick={() => setActiveTab(item.id)}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
                {item.badge > 0 && <span className="nav-badge">{item.badge}</span>}
              </button>
            ))}
            <button className="nav-item logout" onClick={handleLogout}>
              <span className="nav-icon">🚪</span><span className="nav-label">Logout</span>
            </button>
          </nav>
        </aside>

        <main className="profile-main">
          <div style={BLOCK}>
            {activeTab === 'overview'         && <OverviewTab testDrivesCount={testDrivesCount} shortlistedCount={localShortlistedCount} bookingsCount={bookingsCount} sellRequestsCount={sellRequestsCount} ordersCount={ordersCount} />}
            {activeTab === 'saved-cars'       && <SavedCarsTab onShortlistUpdate={handleShortlistUpdate} />}
            {activeTab === 'test-drives'      && <TestDrivesTab />}
            {activeTab === 'bookings'         && <BookingsTab />}
            {activeTab === 'my-orders'        && <MyOrdersTab />}
            {activeTab === 'payment-history'  && <PaymentHistoryTab />}
            {activeTab === 'car-inspection'   && <MySells />}
            {activeTab === 'price-acceptance' && <PriceAcceptanceTab />}
            {activeTab === 'settings'         && <SettingsTab user={user} />}
            {activeTab === 'refer-earn'       && <ReferAndEarn />}
          </div>
        </main>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   OVERVIEW TAB
════════════════════════════════════════════════════════════ */
const OverviewTab = ({ testDrivesCount, shortlistedCount, bookingsCount, sellRequestsCount, ordersCount }) => {
  const [activities, setActivities] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(true);

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        const res = await usersAPI.getRecentActivity();
        setActivities(res.data.activities || []);
      } catch {
        setActivities([]);
      } finally {
        setLoadingActivity(false);
      }
    };
    fetchActivity();
  }, []);

  const iconMap = { heart: '❤️', car: '🚗', calendar: '📅', search: '🔍' };

  return (
    <div style={BLOCK} className="tab-content">
      <div className="tab-header">
        <h2 className="tab-title">Account Overview</h2>
        <p className="tab-subtitle">Here's a summary of your activity</p>
      </div>
      <div className="overview-grid">
        {[
          { icon: '❤️', color: 'pink',   count: shortlistedCount, label: 'Saved Cars'      },
          { icon: '📅', color: 'teal',   count: bookingsCount,    label: 'Active Bookings' },
          { icon: '🚗', color: 'yellow', count: testDrivesCount,  label: 'Test Drives'     },
          { icon: '🛒', color: 'green',  count: ordersCount,      label: 'My Orders'       },
        ].map((card, i) => (
          <div key={i} className={`overview-card card-${card.color}`}>
            <div className="overview-card-icon">{card.icon}</div>
            <div className="overview-card-body">
              <span className="overview-count">{card.count}</span>
              <span className="overview-label">{card.label}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="recent-activity-section">
        <h3 className="section-title">Recent Activity</h3>
        {loadingActivity ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#aaa' }}>Loading activity...</div>
        ) : activities.length === 0 ? (
          <div className="activity-empty"><span>📭</span><p>No recent activity yet. Start browsing cars!</p></div>
        ) : (
          <div className="activity-list">
            {activities.map((item, i) => (
              <div key={i} className="activity-item">
                <div className="activity-icon-wrap">{iconMap[item.icon] || '📌'}</div>
                <div className="activity-body">
                  <p className="activity-text">{item.text}</p>
                  <span className="activity-time">{item.time}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="quick-actions">
        <h3 className="section-title" style={{ marginBottom: 14 }}>Quick Actions</h3>
        <div className="quick-actions-grid">
          {[
            { icon: '🚗', label: 'Browse Cars',     href: '/buy-cars'  },
            { icon: '💰', label: 'Sell Your Car',   href: '/sell-car'  },
            { icon: '🔧', label: 'Service a Car',   href: '/services'  },
            { icon: '📞', label: 'Contact Support', href: '/contact'   },
          ].map((a, i) => (
            <button key={i} className="quick-action-btn" onClick={() => window.location.href = a.href}>
              <span className="qa-icon">{a.icon}</span>
              <span className="qa-label">{a.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   SAVED CARS TAB
════════════════════════════════════════════════════════════ */
const SavedCarsTab = ({ onShortlistUpdate }) => {
  const { refreshShortlist } = useShortlist();
  const [shortlistedCars, setShortlistedCars] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination]   = useState(null);
  const [removingId, setRemovingId]   = useState(null);

  useEffect(() => { loadShortlistedCars(); }, [currentPage]);

  const loadShortlistedCars = async () => {
    try {
      setLoading(true);
      const response = await shortlistService.getShortlistedCars(currentPage);
      if (response.success) { setShortlistedCars(response.data); setPagination(response.pagination); }
      else setError('Failed to load shortlisted cars');
    } catch { setError('Error loading shortlisted cars'); }
    finally { setLoading(false); }
  };

  const handleRemoveCar = async (carId) => {
    try {
      setRemovingId(carId);
      await shortlistService.removeFromShortlist(carId);
      await refreshShortlist(); onShortlistUpdate();
      setShortlistedCars(shortlistedCars.filter(item => item.car.id !== carId));
      if (pagination) setPagination({ ...pagination, total_count: Math.max(0, pagination.total_count - 1) });
    } catch { alert('Failed to remove car from shortlist.'); }
    finally { setRemovingId(null); }
  };

  if (loading) return <LoadingState text="Loading your saved cars..." />;
  if (error)   return <ErrorState message={error} onRetry={loadShortlistedCars} />;

  return (
    <div style={BLOCK} className="tab-content">
      <div className="tab-header">
        <h2 className="tab-title">Saved Cars</h2>
        <p className="tab-subtitle">{pagination?.total_count || 0} car{pagination?.total_count === 1 ? '' : 's'} shortlisted</p>
      </div>
      {shortlistedCars.length === 0 ? (
        <EmptyState icon="❤️" title="No Saved Cars Yet" desc="Start exploring and add cars to your shortlist" btnText="Browse Cars" btnHref="/buy-cars" />
      ) : (
        <>
          <div style={COLLIST}>
            {shortlistedCars.map(item => {
              const car    = item.car;
              const imgSrc = getImageUrl(car.images_urls?.[0]);
              return (
                <div key={item.id} className="sv-card">
                  <div className="sv-card-img">
                    {imgSrc
                      ? <img src={imgSrc} alt={`${car.brand} ${car.model}`} onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                      : null}
                    <div className="sv-card-no-img" style={{ display: imgSrc ? 'none' : 'flex' }}>🚗</div>
                  </div>
                  <div className="sv-card-body">
                    <div className="sv-card-top">
                      <div>
                        <h3 className="sv-card-name" onClick={() => window.location.href = `/car/${car.id}`}>
                          {car.brand} {car.model} {car.year && <span className="sv-year">{car.year}</span>}
                        </h3>
                        <div className="sv-specs">
                          {car.km_driven    && <span>🛣️ {Number(car.km_driven).toLocaleString('en-IN')} km</span>}
                          {car.fuel_type    && <span>⛽ {car.fuel_type}</span>}
                          {car.transmission && <span>⚙️ {car.transmission}</span>}
                          {car.location     && <span>📍 {car.location}</span>}
                        </div>
                      </div>
                      <button className="sv-remove-btn" onClick={() => handleRemoveCar(car.id)} disabled={removingId === car.id}>
                        {removingId === car.id ? '...' : '❤️'}
                      </button>
                    </div>
                    <div className="sv-card-bottom">
                      <span className="sv-price">₹{parseFloat(car.price).toLocaleString('en-IN')}</span>
                      <button className="sv-view-btn" onClick={() => window.location.href = `/car/${car.id}`}>View Details →</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {pagination?.total_pages > 1 && (
            <div className="pagination-row">
              <button className="pg-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>← Prev</button>
              <span className="pg-info">Page {currentPage} of {pagination.total_pages}</span>
              <button className="pg-btn" onClick={() => setCurrentPage(p => Math.min(pagination.total_pages, p + 1))} disabled={currentPage === pagination.total_pages}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   TEST DRIVES TAB
════════════════════════════════════════════════════════════ */
const TestDrivesTab = () => {
  const [testDrives, setTestDrives]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [selectedTestDrive, setSelectedTestDrive]         = useState(null);
  const [actionLoading, setActionLoading]                 = useState(null);

  useEffect(() => { fetchTestDrivesData(); }, []);

  const fetchTestDrivesData = async () => {
    try { setLoading(true); const data = await fetchUserTestDrives(); setTestDrives(data); }
    catch { setError('Failed to load test drives'); }
    finally { setLoading(false); }
  };

  const handleRescheduleSubmit = async (formData) => {
    try {
      await rescheduleTestDrive(selectedTestDrive.id, formData);
      setIsRescheduleModalOpen(false); setSelectedTestDrive(null);
      await fetchTestDrivesData(); alert('Test drive rescheduled successfully!');
    } catch (err) { throw err; }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this test drive?')) return;
    try { setActionLoading(id); await cancelTestDrive(id); await fetchTestDrivesData(); alert('Test drive cancelled successfully!'); }
    catch { alert('Failed to cancel test drive.'); }
    finally { setActionLoading(null); }
  };

  const fmt      = (d) => new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  const fmtTime  = (t) => { const [h, m] = t.split(':'); const hr = parseInt(h); return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`; };
  const statusClass = (s) => ({ pending: 'status-pending', confirmed: 'status-confirmed', cancelled: 'status-cancelled', completed: 'status-completed', requested: 'status-requested' }[s?.toLowerCase()] || 'status-pending');

  if (loading) return <LoadingState text="Loading test drives..." />;
  if (error)   return <ErrorState message={error} onRetry={fetchTestDrivesData} />;

  return (
    <div style={BLOCK} className="tab-content">
      <div className="tab-header">
        <h2 className="tab-title">My Test Drives</h2>
        <p className="tab-subtitle">{testDrives.length === 0 ? 'No test drives scheduled yet' : `${testDrives.length} test drive${testDrives.length > 1 ? 's' : ''} scheduled`}</p>
      </div>
      {testDrives.length === 0
        ? <EmptyState icon="🚗" title="No Test Drives Yet" desc="Schedule a test drive for your dream car" btnText="Browse Cars" btnHref="/buy-cars" />
        : <div style={COLLIST}>
            {testDrives.map(td => {
              const disabled = td.status === 'cancelled' || td.status === 'completed';
              return (
                <div key={td.id} className="td-card">
                  <div className="td-card-top">
                    <div className="td-car-info">
                      <h3 className="td-car-title">{td.car?.title || `${td.car?.brand} ${td.car?.model}`}</h3>
                      <p className="td-car-sub">{td.car?.brand} {td.car?.model}</p>
                    </div>
                    <span className={`td-status ${statusClass(td.status)}`}>
                      {td.status ? td.status.charAt(0).toUpperCase() + td.status.slice(1) : 'Pending'}
                    </span>
                  </div>
                  <div className="td-details-grid">
                    <div className="td-detail-item"><span className="td-detail-icon">📅</span><div><span className="td-detail-label">Date & Time</span><span className="td-detail-val">{fmt(td.preferred_date)} at {fmtTime(td.preferred_time)}</span></div></div>
                    <div className="td-detail-item"><span className="td-detail-icon">📍</span><div><span className="td-detail-label">Location</span><span className="td-detail-val">{td.location}</span></div></div>
                    <div className="td-detail-item"><span className="td-detail-icon">🎯</span><div><span className="td-detail-label">Mode</span><span className="td-detail-val">{td.mode === 'home' ? '🏠 Home Visit' : '🏢 Visit Hub'}</span></div></div>
                    <div className="td-detail-item"><span className="td-detail-icon">💰</span><div><span className="td-detail-label">Car Price</span><span className="td-detail-val td-price">₹{parseFloat(td.car?.price || 0).toLocaleString('en-IN')}</span></div></div>
                  </div>
                  <div className="td-actions">
                    <button className="btn-reschedule" disabled={disabled || actionLoading === td.id} onClick={() => { setSelectedTestDrive(td); setIsRescheduleModalOpen(true); }}>📅 Reschedule</button>
                    <button className="btn-danger" disabled={disabled || actionLoading === td.id} onClick={() => handleCancel(td.id)}>
                      {actionLoading === td.id ? <><span className="spinner-small"></span> Cancelling...</> : '❌ Cancel'}
                    </button>
                  </div>
                  <div className="td-footer"><span>Booked on {fmt(td.created_at)}</span></div>
                </div>
              );
            })}
            <div className="browse-cta-card">
              <div className="browse-cta-card-text"><h3>Looking for more cars?</h3><p>Schedule another test drive whenever you like.</p></div>
              <button className="btn-primary" onClick={() => window.location.href = '/buy-cars'}>Browse Cars</button>
            </div>
            <RescheduleModal isOpen={isRescheduleModalOpen} onClose={() => { setIsRescheduleModalOpen(false); setSelectedTestDrive(null); }} testDrive={selectedTestDrive} onSubmit={handleRescheduleSubmit} />
          </div>
      }
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   BOOKINGS TAB
════════════════════════════════════════════════════════════ */
const BookingsTab = () => {
  const [bookings, setBookings]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [cancellingId, setCancellingId] = useState(null);

  useEffect(() => { fetchBookings(); }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const data = await bookingService.getAllBookings();
      const list = data.bookings || data || [];
      setBookings(Array.isArray(list) ? list : []);
    } catch { setError('Failed to load your bookings'); }
    finally { setLoading(false); }
  };

  const handleCancel = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    try { setCancellingId(bookingId); await bookingService.cancelBooking(bookingId); await fetchBookings(); alert('Booking cancelled successfully!'); }
    catch { alert('Failed to cancel booking.'); }
    finally { setCancellingId(null); }
  };

  const fmt = (d) => { if (!d) return '—'; return new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }); };
  const getStatusInfo = (s) => ({ pending: { label: 'Pending', cls: 'status-pending' }, confirmed: { label: 'Confirmed', cls: 'status-confirmed' }, cancelled: { label: 'Cancelled', cls: 'status-cancelled' }, completed: { label: 'Completed', cls: 'status-completed' } }[s?.toLowerCase()] || { label: 'Pending', cls: 'status-pending' });

  if (loading) return <LoadingState text="Loading your bookings..." />;
  if (error)   return <ErrorState message={error} onRetry={fetchBookings} />;

  return (
    <div style={BLOCK} className="tab-content">
      <div className="tab-header">
        <h2 className="tab-title">My Bookings</h2>
        <p className="tab-subtitle">{bookings.length === 0 ? 'No bookings yet' : `${bookings.length} booking${bookings.length > 1 ? 's' : ''} found`}</p>
      </div>
      {bookings.length === 0
        ? <EmptyState icon="📅" title="No Bookings Yet" desc="Book a car to reserve it before buying" btnText="Buy Cars" btnHref="/buy-cars" />
        : <div style={COLLIST}>
            {bookings.map(booking => {
              const st = getStatusInfo(booking.status);
              const isCancelled = booking.status === 'cancelled' || booking.status === 'completed';
              const car = booking?.car || {};
              const bookingAmt      = Number(booking?.amount_cents ?? 0) / 100;
              const carPrice        = Number(car?.price ?? 0);
              const remainingAmount = Math.max(0, carPrice - bookingAmt);
              const imgSrc = getImageUrl(car.images_urls?.[0]);
              return (
                <div key={booking.id} className="booking-card">
                  <div className="booking-card-header">
                    <div className="booking-car-info">
                      {imgSrc
                        ? <img src={imgSrc} alt={car.title} className="booking-car-thumb" onError={e => { e.target.style.display = 'none'; }} />
                        : <div className="booking-car-thumb-placeholder">🚗</div>}
                      <div>
                        <h3 className="booking-car-name">{car.title || `${car.brand} ${car.model}`}</h3>
                        <p className="booking-car-sub">{car.brand} {car.model} {car.year && `• ${car.year}`}</p>
                      </div>
                    </div>
                    <span className={`td-status ${st.cls}`}>{st.label}</span>
                  </div>
                  <div className="booking-details-grid">
                    <div className="booking-detail"><span className="booking-detail-label">Booking ID</span><span className="booking-detail-val">#{booking.id}</span></div>
                    <div className="booking-detail"><span className="booking-detail-label">Booked On</span><span className="booking-detail-val">{fmt(booking.created_at)}</span></div>
                    <div className="booking-detail"><span className="booking-detail-label">Reserved Until</span><span className="booking-detail-val">{fmt(booking.scheduled_at || booking.expires_at)}</span></div>
                    <div className="booking-detail"><span className="booking-detail-label">Location</span><span className="booking-detail-val">{car.location || '—'}</span></div>
                  </div>
                  <div className="booking-price-strip">
                    <div className="booking-price-item"><span>Car Price</span><strong>₹{carPrice.toLocaleString('en-IN')}</strong></div>
                    <div className="booking-price-divider"></div>
                    <div className="booking-price-item booking-paid"><span>Amount Paid</span><strong>₹{bookingAmt.toLocaleString('en-IN')}</strong></div>
                    <div className="booking-price-divider"></div>
                    <div className="booking-price-item booking-remaining"><span>Remaining</span><strong>₹{remainingAmount.toLocaleString('en-IN')}</strong></div>
                  </div>
                  <div className="booking-actions">
                    <button className="btn-view-car" onClick={() => window.location.href = `/car/${car.id}`}>View Car →</button>
                    <button className="btn-danger" disabled={isCancelled || cancellingId === booking.id} onClick={() => handleCancel(booking.id)}>
                      {cancellingId === booking.id ? <><span className="spinner-small"></span> Cancelling...</> : '✕ Cancel Booking'}
                    </button>
                  </div>
                </div>
              );
            })}
            <div className="browse-cta-card" style={{ marginTop: 8 }}>
              <div className="browse-cta-card-text"><h3>Ready to book another car?</h3><p>Browse our entire collection.</p></div>
              <button className="btn-primary" onClick={() => window.location.href = '/buy-cars'}>Browse Cars</button>
            </div>
          </div>
      }
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   SETTINGS TAB
════════════════════════════════════════════════════════════ */
const SettingsTab = ({ user }) => {
  const { updateUser } = useAuth();
  const [loading, setLoading]             = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [documents, setDocuments]         = useState([]);
  const [formData, setFormData] = useState({
    name:    user.name    || '',
    email:   (user.email && !user.email.includes('temp.aucauto.com')) ? user.email : '',
    phone:   user.phone   || '',
    address: user.address || '',
  });

  useEffect(() => { fetchDocuments(); }, []);

  const fetchDocuments = async () => {
    try { const response = await usersAPI.getDocuments(); setDocuments(response.data.documents || []); }
    catch {}
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const payload = { name: formData.name, phone: formData.phone, address: formData.address };
      if (formData.email && !formData.email.includes('temp.aucauto.com')) payload.email = formData.email;
      const response = await usersAPI.updateProfile(payload);
      updateUser({ ...user, ...response.data.user }); alert('Profile updated successfully!');
    } catch (error) { alert(error.response?.data?.errors?.join(', ') || 'Failed to update profile'); }
    finally { setLoading(false); }
  };

  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return; setUploadingDocs(true);
    try { await usersAPI.uploadDocuments(files); alert('Documents uploaded successfully!'); await fetchDocuments(); e.target.value = ''; }
    catch { alert('Failed to upload documents'); }
    finally { setUploadingDocs(false); }
  };

  const handleViewDocument   = (url) => window.open(url, '_blank', 'noopener,noreferrer');
  const handleRemoveDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try { const r = await usersAPI.removeDocument(docId); if (r.data.success) setDocuments(prev => prev.filter(d => d.id !== docId)); }
    catch (error) { alert(error.response?.data?.message || 'Failed to delete document'); }
  };

  return (
    <div style={BLOCK} className="tab-content">
      <div className="tab-header">
        <h2 className="tab-title">Account Settings</h2>
        <p className="tab-subtitle">Manage your personal information and preferences</p>
      </div>
      <form className="settings-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <h3 className="section-title">Personal Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Full Name <span className="optional-badge">Optional</span></label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Enter your name" />
            </div>
            <div className="form-group">
              <label>Phone Number <span className="required-badge">Required</span></label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} disabled={!!user.phone} />
              {user.phone && <small>Phone number cannot be changed</small>}
            </div>
          </div>
          <div className="form-group">
            <label>Email Address <span className="optional-badge">Optional</span></label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Enter your email"
              disabled={user.email && !user.email.includes('temp.aucauto.com')} />
            {user.email && !user.email.includes('temp.aucauto.com') && <small>Email cannot be changed</small>}
          </div>
          <div className="form-group">
            <label>Address</label>
            <textarea name="address" value={formData.address} onChange={handleChange} placeholder="Enter your full address" rows="3" className="form-textarea" />
          </div>
        </div>
        <div className="form-section">
          <h3 className="section-title">Documents</h3>
          <div className="form-group">
            <label>Upload Documents (Driving License, Aadhaar, etc.)</label>
            <div className="file-upload-area">
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple onChange={handleDocumentUpload} disabled={uploadingDocs} className="file-input-hidden" id="doc-upload" />
              <label htmlFor="doc-upload" className="file-upload-label">
                {uploadingDocs ? <><span className="spinner-small"></span> Uploading...</> : '📁 Choose Files'}
              </label>
              <span className="file-upload-hint">PDF, JPG, PNG supported</span>
            </div>
          </div>
          {documents.length > 0 && (
            <div className="doc-list">
              {documents.map(doc => (
                <div key={doc.id} className="doc-item">
                  <div className="doc-info"><span className="doc-icon">📄</span><span className="doc-name">{doc.filename}</span></div>
                  <div className="doc-actions">
                    <button type="button" className="doc-view-btn" onClick={() => handleViewDocument(doc.url)}>👁 View</button>
                    <button type="button" className="doc-del-btn" onClick={() => handleRemoveDocument(doc.id)}>🗑 Remove</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="form-section">
          <h3 className="section-title">Notification Preferences</h3>
          <div className="checkbox-group">
            <label className="checkbox-label"><input type="checkbox" defaultChecked /><span>SMS alerts for bookings</span></label>
            {formData.email && !formData.email.includes('temp.aucauto.com') && <>
              <label className="checkbox-label"><input type="checkbox" defaultChecked /><span>Email notifications for new cars</span></label>
              <label className="checkbox-label"><input type="checkbox" /><span>Weekly newsletter</span></label>
            </>}
          </div>
        </div>
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? <><span className="spinner-small"></span> Saving...</> : 'Save Changes'}
        </button>
      </form>
    </div>
  );
};

/* ── Shared helpers ── */
const LoadingState = ({ text }) => (
  <div style={BLOCK} className="tab-content">
    <div className="state-center"><div className="spinner-large"></div><p>{text}</p></div>
  </div>
);
const ErrorState = ({ message, onRetry }) => (
  <div style={BLOCK} className="tab-content">
    <div className="state-center">
      <span style={{ fontSize: 48 }}>⚠️</span>
      <h3 style={{ color: '#c30000' }}>{message}</h3>
      <button className="btn-primary" onClick={onRetry}>Retry</button>
    </div>
  </div>
);
const EmptyState = ({ icon, title, desc, btnText, btnHref }) => (
  <div className="empty-state">
    <div className="empty-icon">{icon}</div>
    <h3>{title}</h3>
    <p>{desc}</p>
    <button className="btn-primary" onClick={() => window.location.href = btnHref}>{btnText}</button>
  </div>
);

export default Profile;